/**
 * queue/format.ts
 * Pure display formatters, ported verbatim from the prototype's helpers so the
 * UI strings match the design exactly.
 */

export const AVG_TURN = 9; // Tier-1 deterministic wait basis (min per turn)

/** Deterministic wait estimate: position × avg turn. */
export function waitMin(pos: number): number {
  return pos * AVG_TURN;
}

/** "now" | "~12 min" | "~1h 3m" — matches the prototype's _fmtWait. */
export function fmtWait(m: number): string {
  if (m <= 0) return 'now';
  if (m < 60) return '~' + m + ' min';
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return '~' + h + 'h ' + (mm ? mm + 'm' : '');
}

/** Indian-grouped rupee string, e.g. ₹1,200. */
export function money(n: number): string {
  return '₹' + Number(n).toLocaleString('en-IN');
}

/** Up-to-two-letter uppercase initials from a name. */
export function initials(name: string | null | undefined): string {
  if (!name) return '?'; // null-safe: nameless entries get a neutral glyph
  return name
    .split(' ')
    .map(w => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

/** "98765 43210" grouping for a 10-digit number; pass-through otherwise. */
export function fmtPhone(p: string): string {
  const d = (p || '').replace(/\D/g, '');
  return d.length === 10 ? d.slice(0, 5) + ' ' + d.slice(5) : d;
}

/** Seconds → "m:ss" for live call/chat durations. */
export function fmtDur(sec: number): string {
  sec = Math.max(0, Math.floor(sec));
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m + ':' + String(s).padStart(2, '0');
}

/** First name only (now-serving board privacy). */
export function firstName(n: string): string {
  return n.split(' ')[0];
}

/** Locale time/date helpers used by the board + ledger. */
export function clockHM(now: number): string {
  return new Date(now).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });
}
export function clockHMS(now: number): string {
  return new Date(now).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
}
export function dateLong(now: number): string {
  return new Date(now).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' });
}
